import axiosInstance from '@/lib/axios';

export interface MobileAppVersionCheckResult {
  updateAvailable: boolean;
  forceUpdate: boolean;
  latestVersion: string | null;
  storeUrl: string | null;
}

export interface NativeAppInfo {
  version: string;
  platform: 'ios' | 'android';
  client: string;
}

const SNOOZE_MS = 24 * 60 * 60 * 1000;

export function parseNativeAppInfo(data: unknown): NativeAppInfo | null {
  if (!data || typeof data !== 'object') return null;

  const record = data as Record<string, unknown>;
  if (record.type !== 'NATIVE_APP_INFO') return null;

  const version = record.version;
  const platform = record.platform;
  const client = record.client;

  if (typeof version !== 'string' || !version.trim()) return null;
  if (typeof client !== 'string' || !client.trim()) return null;
  if (platform !== 'ios' && platform !== 'android') return null;

  return {
    version: version.trim(),
    platform,
    client: client.trim(),
  };
}

function snoozeStorageKey(tenantSlug: string, platform: string, latestVersion: string): string {
  return `milora:app-update-snooze:${tenantSlug}:${platform}:${latestVersion}`;
}

export function isAppUpdateSnoozed(
  tenantSlug: string,
  platform: 'ios' | 'android',
  latestVersion: string
): boolean {
  const raw = localStorage.getItem(snoozeStorageKey(tenantSlug, platform, latestVersion));
  if (!raw) return false;

  const snoozedUntil = Number.parseInt(raw, 10);
  if (!Number.isFinite(snoozedUntil)) return false;

  return Date.now() < snoozedUntil;
}

export function snoozeAppUpdate(
  tenantSlug: string,
  platform: 'ios' | 'android',
  latestVersion: string
): void {
  localStorage.setItem(
    snoozeStorageKey(tenantSlug, platform, latestVersion),
    String(Date.now() + SNOOZE_MS)
  );
}

export async function fetchMobileAppVersionCheck(
  platform: 'ios' | 'android',
  version: string
): Promise<MobileAppVersionCheckResult | null> {
  try {
    const { data } = await axiosInstance.get<MobileAppVersionCheckResult>(
      '/api/mobile-apps/version-check',
      {
        params: {
          platform,
          version,
        },
      }
    );
    return data;
  } catch (error) {
    console.error('[App Update] Version check failed:', error);
    return null;
  }
}
